import { useQuery } from '@zg-rentals/react-query5';
import { HpApiInteractionsV3Handler_list_queryOptions } from '@zg-rentals/ts-hotpads-api-client-react-query';
import { HpUserInteraction } from '@zg-rentals/ts-hotpads-api-client';
import type { HpApiResponseDto, HpUserInteractionsResponseDto } from '@zg-rentals/ts-hotpads-api-client';
import type { UserListingType } from '@/src/lib/api/endpoints/user';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';
import { toInteractionCardListing } from '@/src/lib/utils/v3Listings';
import { USER_LISTINGS_TAG } from './queryTags';

import { userListingKeys } from './queryKeys';

export type UserListingsData = {
  listings: Array<SummaryListingDetails>;
  totalCount: number;
};

/**
 * `UserListingType` and `HpUserInteraction` use different persistent strings
 * (`hidden`/`hide`, `viewed`/`view`), so they must be mapped by value. hotpads-api does
 * the same on its side (`HpUserInteractionsService.toUserListingType`).
 */
export const INTERACTION_BY_TYPE: Record<UserListingType, HpUserInteraction> = {
  favorite: HpUserInteraction.FAVORITE,
  hidden: HpUserInteraction.HIDE,
  inquiry: HpUserInteraction.INQUIRY,
  viewed: HpUserInteraction.VIEW,
};

/** V2 sent 50; the V3 endpoint defaults to 10, so it has to be passed explicitly. */
const PAGE_SIZE = 50;

/**
 * Module-level so the reference is stable. React Query only reuses a memoized `select`
 * result when the function is reference-equal, and `useBaseQuery` rebuilds its options
 * every render — an inline closure here would re-map every card on each render of
 * `ListingActionProvider` and `MapClient`.
 */
function selectUserListings(response: HpApiResponseDto<HpUserInteractionsResponseDto>): UserListingsData {
  // A V3 envelope reports failures as `error` on a 200 and the generated client hands it
  // back unwrapped, so this has to throw rather than fall through to an empty list —
  // otherwise the pages' error branches are unreachable and a failure looks like "no
  // saved listings".
  if (response.error) {
    throw new Error(`user interactions list failed: ${JSON.stringify(response.error)}`);
  }
  const listings = (response.data?.listings ?? []).map(toInteractionCardListing);
  // V3 paginates by page and exposes `totalPages`, with no total-item count. No consumer
  // reads `totalCount`, so it keeps reporting what was actually returned.
  return { listings, totalCount: listings.length };
}

export function useUserListings(type: UserListingType, options?: { enabled?: boolean }) {
  return useQuery({
    // The generated `useHpApiInteractionsV3Handler_list` hook can't be used directly:
    // it pins both the query key and TData, so it can neither carry `userListingKeys`
    // (which `removeUserScopedQueries` and `useHdpContactSubmission` invalidate by key)
    // nor reshape the envelope. Spreading its queryOptions keeps codegen owning the
    // request while leaving the key and projection to us.
    ...HpApiInteractionsV3Handler_list_queryOptions({
      interactions: INTERACTION_BY_TYPE[type],
      // `activeOnly` is deliberately not sent, matching the V2 call it replaces: an
      // off-market listing you saved or viewed stays in your list, with `active` from
      // the card tags suppressing its contact actions.
      pageSize: PAGE_SIZE,
    }),
    queryKey: userListingKeys.byType(type),
    enabled: options?.enabled !== false,
    staleTime: 0,
    select: selectUserListings,
    meta: {
      tags: [USER_LISTINGS_TAG],
      context: {
        api: 'hotpads-api',
        details: `fetch ${type} user listings`,
      },
    },
  });
}
